/**
 * AuthContext
 * 
 * Wraps Auth0 SDK and provides authentication state + helpers to the app.
 * Handles login, logout, token management, and user profile.
 */

import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useAuth0, Auth0Provider, AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

// Auth0 configuration — sourced from environment variables at build time
const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN || '';
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
const AUTH0_REDIRECT_URI = window.location.origin + '/callback';
const AUTH_EXPIRED_EVENT = 'bringiq:auth-expired';

// ----- Types -----

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  avatar?: string;
  role: string;
  organizationId: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  login: () => void;
  loginWithGoogle: () => void;
  loginWithMicrosoft: () => void;
  signup: () => void;
  logout: () => void;
  getToken: () => Promise<string>;
}

// ----- Context -----

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ----- Inner provider that consumes useAuth0 -----

const AuthContextInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getIdTokenClaims,
  } = useAuth0();

  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Build our normalised user object from Auth0 profile
  const user: AuthUser | null = auth0User
    ? {
        userId: auth0User.sub || '',
        email: auth0User.email || '',
        name: auth0User.name || auth0User.nickname || '',
        avatar: auth0User.picture,
        role: (auth0User as Record<string, unknown>)['https://bringiq.dev/role'] as string || 'member',
        organizationId:
          ((auth0User as Record<string, unknown>)['https://bringiq.dev/organizationId'] as string) ||
          (auth0User.org_id as string) ||
          'default',
      }
    : null;

  // Fetch & cache ID token whenever auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      getIdTokenClaims()
        .then((claims) => {
          if (claims?.__raw) {
            setAccessToken(claims.__raw);
            localStorage.setItem('bringiq_token', claims.__raw);
          }
        })
        .catch((err) => {
          console.error('Failed to get ID token', err);
        });
    } else if (!isLoading) {
      setAccessToken(null);
      localStorage.removeItem('bringiq_token');
    }
  }, [isAuthenticated, isLoading, getIdTokenClaims]);

  /** Get a fresh ID token */
  const getToken = useCallback(async (): Promise<string> => {
    const claims = await getIdTokenClaims();
    const token = claims?.__raw || '';
    setAccessToken(token);
    localStorage.setItem('bringiq_token', token);
    return token;
  }, [getIdTokenClaims]);

  const login = useCallback(
    () => {
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: AUTH0_REDIRECT_URI,
        },
      });
    },
    [loginWithRedirect]
  );

  const loginWithGoogle = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: AUTH0_REDIRECT_URI,
        connection: 'google-oauth2',
        prompt: 'select_account',
      },
    });
  }, [loginWithRedirect]);
  const loginWithMicrosoft = useCallback(() => login(), [login]);

  const signup = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        redirect_uri: AUTH0_REDIRECT_URI,
      },
    });
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    localStorage.removeItem('bringiq_token');
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
  }, [auth0Logout]);

  useEffect(() => {
    const onAuthExpired = () => {
      localStorage.removeItem('bringiq_token');
      auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    };
  }, [auth0Logout]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        accessToken,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        signup,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ----- Auth0 wrapper provider -----

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback((appState?: AppState) => {
    navigate(appState?.returnTo || '/', { replace: true });
  }, [navigate]);

  // If Auth0 is not configured, render children with a no-op context (dev mode)
  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) {
    return (
      <AuthContext.Provider
        value={{
          isAuthenticated: true,
          isLoading: false,
          user: {
            userId: 'demo-user',
            email: 'demo@bringiq.dev',
            name: 'Demo User',
            role: 'admin',
            organizationId: 'demo-org',
          },
          accessToken: 'demo-token',
          login: () => {},
          loginWithGoogle: () => {},
          loginWithMicrosoft: () => {},
          signup: () => {},
          logout: () => {},
          getToken: async () => 'demo-token',
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: AUTH0_REDIRECT_URI,
        scope: 'openid profile email',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      <AuthContextInner>{children}</AuthContextInner>
    </Auth0Provider>
  );
};

// ----- Hook -----

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
